import { AdmissionAdditionalInformation } from "@/server/backend-api/generated";
import Button from "../Button";
import DatePicker from "../Input/DatePicker";
import Dropdown from "../Input/Dropdown";
import TextField from "../Input/TextField";
import { Dialog } from "./Dialog";
import AutoComplete from "../Input/AutoComplete";
import React from "react";
import PhoneNumberInput from "../PhoneNumberInput";

type BaseProps = {
  open: boolean;
  onClose: () => void;
  onCancelClick?: () => void;
  title: string;
  description: React.ReactNode;
  confirmActionText: string;
  cancelActionText?: string;
  isLoading?: boolean;
  removeCancel?: boolean;
};

type OnClickConfirmProps = BaseProps & {
  confirmType: "click";
  onConfirmClick: () => void;
  fields?: AdmissionAdditionalInformation[];
  setFields?: React.Dispatch<
    React.SetStateAction<AdmissionAdditionalInformation[]>
  >;
};

type HrefConfirmProps = BaseProps & {
  confirmType: "href";
  onConfirmHref: string;
  fields?: AdmissionAdditionalInformation[];
  setFields?: React.Dispatch<
    React.SetStateAction<AdmissionAdditionalInformation[]>
  >;
};

export function ConfirmDialog(props: OnClickConfirmProps | HrefConfirmProps) {
  const handleInputChange = (index: number, newValue: string) => {
    if (props.fields && props.setFields) {
      const newFields = [...props.fields];
      newFields[index].value = newValue;
      props.setFields(newFields);
    }
  };

  const allValuesFilled = props.fields
    ? props.fields.every((data) => !data.canBeEdited || data.value !== "")
    : false;

  return (
    <>
      {!props.fields ? (
        <Dialog {...props}>
          <div className="flex justify-end space-x-md mt-md">
            {!props.removeCancel && (
              <Button
                kind="link"
                className="ml-sm"
                onClick={props.onCancelClick}
                disabled={props.isLoading === true}
                data-testid={"confirm-dialog-cancel-btn"}
              >
                {props.cancelActionText ?? "Cancel"}
              </Button>
            )}
            {props.confirmType === "click" ? (
              <Button
                kind="secondary"
                className="ml-sm mr-sm"
                onClick={props.onConfirmClick}
                disabled={props.isLoading === true}
                data-testid={"confirm-dialog-confirm-btn"}
              >
                {props.confirmActionText}
              </Button>
            ) : (
              <Button
                kind="secondary"
                className="ml-sm mr-sm"
                href={props.onConfirmHref}
                data-testid={"confirm-dialog-confirm-btn"}
              >
                {props.confirmActionText}
              </Button>
            )}
          </div>
        </Dialog>
      ) : (
        <>
          <Dialog {...props}>
            <div className="flex flex-col">
              {props.fields.map((data, index) => (
                <div key={index}>
                  {data.type === "longlist" && (
                    <AutoComplete
                      className="mt-sm col-span-2 laptop:col-span-1"
                      labelProps={{
                        text: data.name ? data.name : "",
                      }}
                      onChange={(e) => {
                        handleInputChange(index, e?.data ? e?.data : "");
                      }}
                      items={
                        typeof data.list === "object" && data.list !== null
                          ? data.list.map((item, index) => ({
                              text: item.value ?? "",
                              id: index,
                              data: item.key ?? "",
                              preSelected: item.key === data.value,
                            }))
                          : []
                      }
                      disabled={!data.canBeEdited}
                      hideDropdownButton={true}
                      isSearch={true}
                      isMandatory={true}
                    />
                  )}
                  {data.type === "list" && (
                    <Dropdown
                      className="mt-sm col-span-2 laptop:col-span-1"
                      labelProps={{
                        text: data.name ? data.name : "",
                      }}
                      onChange={(e) =>
                        handleInputChange(index, e?.data ? e?.data : "")
                      }
                      items={
                        typeof data.list === "object" && data.list !== null
                          ? data.list.map((item, index) => ({
                              text: item.value ?? "",
                              id: index,
                              data: item.key ?? "",
                              preSelected: item.key === data.value,
                            }))
                          : []
                      }
                      disabled={!data.canBeEdited}
                      isMandatory={true}
                    />
                  )}{" "}
                  {(data.type === "number" || data.type === "text") && (
                    <TextField
                      maxLength={100}
                      className={"mt-sm col-span-full laptop:col-span-1"}
                      labelProps={{
                        text: data.name ? data.name : "",
                      }}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      type={data.type}
                      value={data.value ?? ""}
                      disabled={!data.canBeEdited}
                      isMandatory={true}
                    />
                  )}
                  {data.type === "date" && (
                    <DatePicker
                      className="mt-sm col-span-2 laptop:col-span-1"
                      onChange={(e) =>
                        handleInputChange(index, e?.toISOString() ?? "")
                      }
                      disabled={!data.canBeEdited}
                      labelProps={{
                        text: data.name ? data.name : "",
                      }}
                      date={data.value ? new Date(data.value) : undefined}
                      showFutureYears={true}
                      isMandatory={true}
                    />
                  )}
                  {data.type === "phonenumber" && (
                    <div className="mt-sm">
                      <PhoneNumberInput
                        value={data.value ?? undefined}
                        disabled={!data.canBeEdited}
                        onChange={(value) => handleInputChange(index, value)}
                      />
                    </div>
                  )}
                </div>
              ))}
              <div className="flex justify-end space-x-md mt-md">
                <Button
                  kind="link"
                  className="ml-sm"
                  onClick={props.onCancelClick}
                  disabled={props.isLoading === true}
                  data-testid={"confirm-dialog-cancel-btn"}
                >
                  {props.cancelActionText ?? "Cancel"}
                </Button>
                {props.confirmType === "click" ? (
                  <Button
                    kind="secondary"
                    className="ml-sm mr-sm"
                    onClick={props.onConfirmClick}
                    disabled={props.isLoading === true || !allValuesFilled}
                    data-testid={"confirm-dialog-confirm-btn"}
                  >
                    {props.confirmActionText}
                  </Button>
                ) : (
                  <Button
                    kind="secondary"
                    className="ml-sm mr-sm"
                    href={props.onConfirmHref}
                    data-testid={"confirm-dialog-confirm-btn"}
                  >
                    {props.confirmActionText}
                  </Button>
                )}
              </div>
            </div>
          </Dialog>
        </>
      )}
    </>
  );
}
